import "./aiforme.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Container, Row } from "@amzn/stencil-react-components/layout";
import { RenderModalFunction, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { AiForMeHeaderBanner } from "common/components/AIForMe/AiForMeHeaderBanner";
import { AiForMeSearchList } from "common/components/AIForMe/AiForMeSearchList";
import { CompareBar, CompareFlyout } from "common/components/AIForMe/Compare";
import { useAiForMeSearchList } from "common/components/AIForMe/useAiForMeSearchList";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ModalWhatsNew } from "common/components/ModalWhatsNew/ModalWhatsNew";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { PolicyModal } from "common/components/PolicyModal/PolicyModal";
import { CompareProvider, useCompare } from "common/context/compare";
import { COOKIE_NAMES, getCookie, setCookie } from "common/helpers/cookie-utils";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC, useEffect } from "react";
import { useHistory } from "react-router-dom";

const TIMEOUT_TO_DISPLAY_DISCLAIMER = 500; // ms

const pageSectionPadding = {
  paddingTop: 32,
  paddingBottom: 32,
};

/**
 * Inner component that uses compare context
 */
const AiForMeContent: FC = () => {
  const history = useHistory();
  let openHandler: () => void | undefined;

  const { addErrorBanner } = useMessageBannersBox();
  const { inputs, updateInputs, batchUpdateInputs } = useAiForMeSearchList();

  // Compare functionality
  const { selectedTools, isFlyoutOpen, canCompare, removeTool, clearAll, openFlyout, closeFlyout } = useCompare();

  // Check permissions on component mount
  useEffect(() => {
    document.title = getString("aiForMe.title");

    if (!UserPermissions.current.hasAuthorized) {
      // User hasn't been authorized yet, wait for authorization
      return;
    }

    if (!UserPermissions.current.hasPermission(PERMISSIONS.AI_FOR_ME)) {
      // Only show error if we have valid permissions but user doesn't have this specific permission
      // If permissions are expired, the UserPermissions service will handle the session expired redirect
      const hasValidPermissions = UserPermissions.current.permissions.length > 0;
      if (hasValidPermissions) {
        addErrorBanner(getString("errors.notAuthorized"), false);
        history.push("/");
      }
    }
  }, []);

  const renderModal: RenderModalFunction = ({ close }: RenderModalFunctionParams) => (
    <PolicyModal
      policy={getString("policy.ai.policies")}
      policyTitle={getString("policy.ai.modal.title")}
      policyType="ai"
      close={close}
    />
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (openHandler && !getCookie(COOKIE_NAMES.AI_POLICY_ACKNOWLEDGED)) {
        openHandler();
      } else {
        setCookie(COOKIE_NAMES.AI_POLICY_ACKNOWLEDGED, "true", 365);
      }
    }, TIMEOUT_TO_DISPLAY_DISCLAIMER);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <WithModal renderModal={renderModal} shouldCloseOnClickOutside={false}>
      {({ open }) => (
        <>
          <ModalWhatsNew />
          <div className="aiforme-gradient-back">
            <PageMainContainer>
              <Row>
                <Breadcrumbs
                  crumbs={[
                    {
                      title: getString("logoText"),
                      url: "/",
                    },
                    {
                      title: getString("aiForMe.title"),
                    },
                  ]}
                  onCrumbClick={
                    /* istanbul ignore next */
                    (e, crumb: CrumbProps) => {
                      if (crumb.url) {
                        e.preventDefault();
                        history.push(crumb.url);
                      }
                    }
                  }
                />
              </Row>
              <AiForMeHeaderBanner
                inputs={inputs}
                updateInputs={updateInputs}
                batchUpdateInputs={batchUpdateInputs}
              ></AiForMeHeaderBanner>
            </PageMainContainer>
          </div>
          <div className="aiforme-content">
            <PageMainContainer>
              <Container {...pageSectionPadding}>
                <AiForMeSearchList inputs={inputs} updateInputs={updateInputs} batchUpdateInputs={batchUpdateInputs} />
              </Container>
            </PageMainContainer>
          </div>

          {/* Compare Bar - sticky bottom banner */}
          <CompareBar
            selectedTools={selectedTools}
            canCompare={canCompare}
            isComparing={isFlyoutOpen}
            onRemoveTool={removeTool}
            onCancel={clearAll}
            onCompareNow={openFlyout}
          />

          {/* Compare Flyout - modal with comparison table */}
          <CompareFlyout isOpen={isFlyoutOpen} tools={selectedTools} onClose={closeFlyout} onRemoveTool={removeTool} />

          {(() => {
            openHandler = open;
            return null;
          })()}
        </>
      )}
    </WithModal>
  );
};

/**
 * Main AiForMe page component wrapped with CompareProvider
 */
const AiForMePage: FC = () => {
  return (
    <CompareProvider>
      <AiForMeContent />
    </CompareProvider>
  );
};

export default AiForMePage;
